// Core API
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  Company,
  Site,
  Warehouse,
  Location,
  CompanyGroup,
  CompanyGroupMembersResponse,
  HierarchyTreeResponse,
  Role,
  UserProfile,
  CompanyUsersAccess,
  DualIdentityState,
  UserManagementLog,
  UserSitePermission,
  SupplierCustomerRelationship,
  CustomerSupplierCode,
  CustomerSupplierCodeCreate,
  CustomerSupplierCodeUpdate,
  DashboardStats,
  CompanyRegistrationData,
  ViewContext,
  ViewContextUpdate,
  FilterVisibilityPayload,
  FilterVisibilityOverrides,
  QueryParams,
  ActionResponse,
  LegalContent,
  LegalAcceptRequest,
  LegalAcceptResponse,
  PrimaryContactAssignment,
  PrimaryContactCompanyOption,
  PrimaryContactSetResponse,
} from './types';

/**
 * A company on the other side of the tenant boundary from the caller.
 *
 * The flat shape `/core/companies/my_connections/` returns — deliberately not
 * a full `Company`, because the endpoint is a picker feed and returns no
 * contact or address data.
 */
export interface ConnectionCompany {
  id: number;
  name: string;
  company_type: string;
  /** Only sent with `include_onboarding`; null for a supplier with no profile. */
  onboarding_status?: 'draft' | 'pending_initial' | 'invited' | 'not_approved' | 'due_diligence' | 'approved' | null;
}


/** Month × currency matrix: the last stored rate of each month. Newest month first. */
export interface MonthlyExchangeRates {
  company: number | null;
  company_name: string | null;
  stored_base_currency: string;           // always GBP — how rates are stored
  base_currency: string;                  // what rate_in_base is expressed in
  months: string[];                       // 'YYYY-MM'
  currencies: string[];
  rates: Record<string, Record<string, {
    rate: string;                         // stored: 1 GBP = rate × currency
    rate_in_base: string | null;          // 1 base_currency = rate_in_base × currency  (base-first)
    inverse_in_base: string | null;       // 1 currency = inverse_in_base × base_currency (as quoted)
    rate_date: string;
    scope: 'customer' | 'platform';
  }>>;
}

/** One stored exchange rate. `rate` is 1 GBP = rate × currency. */
export interface ExchangeRateRow {
  id: number;
  currency: string;
  currency_display: string;
  rate: string;                           // stored: 1 GBP = rate × currency
  rate_in_base?: string | null;           // 1 base_currency = rate_in_base × currency (base-first; when ?base= given)
  inverse_in_base?: string | null;        // 1 currency = inverse_in_base × base_currency (as quoted)
  base_currency?: string;
  rate_date: string;
  source: string;
  fetched_at: string;
  company: number | null;
  company_name: string | null;
  scope: 'customer' | 'platform';
}

export const coreAPI = {
  // Dashboard
  // CR-3396: Accept view context params for hierarchical filtering
  getDashboardStats: (params?: Record<string, string | number>): Promise<AxiosResponse<DashboardStats>> =>
    api.get('/core/dashboard/', { params }),

  // Companies
  getCompanies: (params?: QueryParams): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/', { params }),

  getCompany: (id: number): Promise<AxiosResponse<Company>> =>
    api.get(`/core/companies/${id}/`),

  // CR-3396: Get companies accessible to the current user (own company + group siblings).
  // CR-4887: pass { context: 'customer_filter' } from the Dashboard / Supplier Performance
  // filter panels so supplier-portal users get the CUSTOMER hierarchy they serve instead
  // of their own group siblings.
  getAccessibleCompanies: (params?: { context?: string }): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/accessible/', { params }),

  // CR-4669: Companies in the current user's group — read-only index used by
  // the Supplier Profile's Group Structure tab. The main /core/companies/
  // list is gated by IsHelixUser, so supplier users get 403; this action is
  // open to any authenticated user but scoped server-side to their own group.
  getMyGroupMembers: (params?: { company_type?: string; group?: number }): Promise<AxiosResponse<{ count: number; results: Company[] }>> =>
    api.get('/core/companies/my_group_members/', { params }),

  // Supplier-Site options for the Dashboard filter dropdown. Portal-aware:
  // customer portal returns suppliers via SupplierCustomerRelationship,
  // supplier portal returns the user's own group siblings (their supplier
  // sites), helix returns all active suppliers.
  // CR-6310: Accepts strategic_only param to filter to strategic suppliers only.
  getSupplierSitesForFilter: (params?: { strategic_only?: boolean }): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/for-filter/', { params }),

  // CR-6037: Customer-safe company list for the Contract Pricing modal's
  // Customer + Supplier dropdowns (create AND edit). The main /core/companies/
  // list is IsHelixUser-only, so customer users saw empty dropdowns. This
  // action is open to any authenticated user but scoped server-side: customers
  // get their own sites + traded suppliers, suppliers get their sites + served
  // customers, helix gets everything. Includes company_type for client filter.
  // CR-6166: pass customerId to narrow the supplier list to that site's
  // approved suppliers (division/group cascade). Omit for the full in-scope set.
  getContractPricingCompanies: (customerId?: number): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/for-contract-pricing/', customerId ? { params: { customer: customerId } } : undefined),

  // Companies the caller may interact with across the tenant boundary — a
  // supplier gets the customers it trades with, a customer gets its approved
  // suppliers. Open to any authenticated user and scoped server-side, which is
  // what the IsHelixUser-gated /core/companies/ list is not. Used by the
  // standalone "New Concession" dialog to pick the customer the deviation is
  // raised against. `include_onboarding` (Questionnaires › Assign) also lists
  // suppliers still being onboarded — never rejected ones — with their status.
  getMyConnections: (params?: { search?: string; limit?: number; include_onboarding?: boolean }): Promise<AxiosResponse<ConnectionCompany[]>> =>
    api.get('/core/companies/my_connections/', { params }),

  // Supplier companies for the User Management "Invite supplier user" picker.
  // The main /core/companies/ list is gated by IsHelixUser, so a customer
  // admin with can_manage_users gets 403; this action is gated by the same
  // can_manage_users permission as the invite feature and returns all active
  // approved suppliers.
  getInvitableSuppliers: (): Promise<AxiosResponse<{ results: Company[] }>> =>
    api.get('/core/companies/for-invite/'),

  // CR-7071: single source for the Add-user wizard's Companies step. Returns the
  // companies the inviting admin may actually grant into (scoped server-side to the
  // same authority the invitation-create floor enforces) plus the CompanyGroups over
  // them, so the group/division shortcuts render. Replaces the wizard's prior
  // getAccessibleCompanies / getInvitableSuppliers + getCompanyGroups combo — the last
  // of which is Helix-only and 403'd for customer admins, silently dropping the group
  // rows. company_type: 'customer' | 'supplier'.
  getInviteOptions: (companyType: 'customer' | 'supplier'): Promise<AxiosResponse<{ companies: Company[]; groups: CompanyGroup[] }>> =>
    api.get('/core/companies/invite-options/', { params: { company_type: companyType } }),

  createCompany: (data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.post('/core/companies/', data),

  updateCompany: (id: number, data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.put(`/core/companies/${id}/`, data),

  // CR-7255: the dual-company marker, platform staff only. Setting it names
  // the customer supplied and the supplier code in the same call.
  getDualIdentity: (id: number): Promise<AxiosResponse<DualIdentityState>> =>
    api.get(`/core/companies/${id}/dual-identity/`),
  setDualIdentity: (
    id: number,
    data: { is_also_supplier: boolean; customer_id?: number; supplier_code?: string },
  ): Promise<AxiosResponse<DualIdentityState>> =>
    api.post(`/core/companies/${id}/dual-identity/`, data),

  patchCompany: (id: number, data: Partial<Company>): Promise<AxiosResponse<Company>> =>
    api.patch(`/core/companies/${id}/`, data),

  deleteCompany: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/companies/${id}/`),

  uploadCompanyLogo: (id: number, file: File): Promise<AxiosResponse<Company>> => {
    const form = new FormData();
    form.append('logo', file);
    return api.post(`/core/companies/${id}/upload-logo/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getCompanySites: (id: number): Promise<AxiosResponse<Site[]>> =>
    api.get(`/core/companies/${id}/sites/`),

  getCompanyUsers: (id: number): Promise<AxiosResponse<UserProfile[]>> =>
    api.get(`/core/companies/${id}/users/`),

  /** CR-7255 review: ask before reading the Users tab, so a refusal is
   *  explained rather than drawn as an empty list. Always 200. */
  getCompanyUsersAccess: (id: number): Promise<AxiosResponse<CompanyUsersAccess>> =>
    api.get(`/core/companies/${id}/users-access/`),

  approveCompanyUsers: (id: number, userIds: number[]): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/companies/${id}/approve_users/`, { user_ids: userIds }),

  // Sites
  getSites: (params?: QueryParams): Promise<AxiosResponse<{ results: Site[] }>> =>
    api.get('/core/sites/', { params }),

  // CR-3396: Get sites accessible to the current user.
  // CR-4887: pass { context: 'customer_filter' } from the Dashboard / Supplier Performance
  // filter panels so supplier-portal users get the CUSTOMER sites they serve instead
  // of their own authorised sites (the user-menu site switcher must NOT pass it).
  getAccessibleSites: (params?: { context?: string }): Promise<AxiosResponse<Site[]>> =>
    api.get('/core/sites/accessible/', { params }),

  getSite: (id: number): Promise<AxiosResponse<Site>> =>
    api.get(`/core/sites/${id}/`),

  createSite: (data: Partial<Site>): Promise<AxiosResponse<Site>> =>
    api.post('/core/sites/', data),

  updateSite: (id: number, data: Partial<Site>): Promise<AxiosResponse<Site>> =>
    api.put(`/core/sites/${id}/`, data),

  deleteSite: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/sites/${id}/`),

  // Warehouses
  getWarehouses: (params?: QueryParams): Promise<AxiosResponse<{ results: Warehouse[] }>> =>
    api.get('/core/warehouses/', { params }),

  // CR-4339: Get warehouses accessible to the current user
  getAccessibleWarehouses: (): Promise<AxiosResponse<Warehouse[]>> =>
    api.get('/core/warehouses/accessible/'),

  getWarehouse: (id: number): Promise<AxiosResponse<Warehouse>> =>
    api.get(`/core/warehouses/${id}/`),

  createWarehouse: (data: Partial<Warehouse>): Promise<AxiosResponse<Warehouse>> =>
    api.post('/core/warehouses/', data),

  updateWarehouse: (id: number, data: Partial<Warehouse>): Promise<AxiosResponse<Warehouse>> =>
    api.put(`/core/warehouses/${id}/`, data),

  deleteWarehouse: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/warehouses/${id}/`),

  // Locations
  getLocations: (params?: QueryParams): Promise<AxiosResponse<{ results: Location[] }>> =>
    api.get('/core/locations/', { params }),

  getLocation: (id: number): Promise<AxiosResponse<Location>> =>
    api.get(`/core/locations/${id}/`),

  createLocation: (data: Partial<Location>): Promise<AxiosResponse<Location>> =>
    api.post('/core/locations/', data),

  updateLocation: (id: number, data: Partial<Location>): Promise<AxiosResponse<Location>> =>
    api.put(`/core/locations/${id}/`, data),

  deleteLocation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/locations/${id}/`),

  // Company Groups
  getCompanyGroups: (params?: QueryParams): Promise<AxiosResponse<{ results: CompanyGroup[] }>> =>
    api.get('/core/company-groups/', { params }),

  getCompanyGroup: (id: number): Promise<AxiosResponse<CompanyGroup>> =>
    api.get(`/core/company-groups/${id}/`),

  // CR-3396: Get the company group accessible to the current user
  getAccessibleCompanyGroup: (): Promise<AxiosResponse<CompanyGroup | null>> =>
    api.get('/core/company-groups/accessible/'),

  // Supplier-Group options for the Dashboard / Supplier-Performance filter
  // dropdown. Pass { context: 'customer_filter' } for supplier-portal users
  // to receive customer-side groups (mirrors CR-4887 on companies/sites).
  getSupplierGroupsForFilter: (
    params?: { context?: 'customer_filter' }
  ): Promise<AxiosResponse<{ results: CompanyGroup[] }>> =>
    api.get('/core/company-groups/for-filter/', { params }),

  // CR-6654: supplier_ids assigns the group's members at create time. A group
  // with no members is invisible (every surface derives groups from the
  // suppliers in them), so the backend requires at least one. company_ids is
  // the side-neutral spelling (customer sites for a customer group); the
  // group's company_type follows the members either way.
  createCompanyGroup: (
    data: Partial<CompanyGroup> & { supplier_ids?: number[]; company_ids?: number[] }
  ): Promise<AxiosResponse<CompanyGroup>> =>
    api.post('/core/company-groups/', data),

  updateCompanyGroup: (id: number, data: Partial<CompanyGroup>): Promise<AxiosResponse<CompanyGroup>> =>
    api.put(`/core/company-groups/${id}/`, data),

  /** Partial update — use when sending a subset of fields, since PUT replaces
   *  the whole representation and would clear anything omitted. */
  patchCompanyGroup: (id: number, data: Partial<CompanyGroup>): Promise<AxiosResponse<CompanyGroup>> =>
    api.patch(`/core/company-groups/${id}/`, data),

  deleteCompanyGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/company-groups/${id}/`),

  updateCompanyGroupPreferences: (id: number, data: Record<string, string | null>): Promise<AxiosResponse<Record<string, string | null>>> =>
    api.patch(`/core/company-groups/${id}/preferences/`, data),

  // CR-4579: Get member companies and aggregate stats for a group
  getCompanyGroupMembers: (id: number): Promise<AxiosResponse<CompanyGroupMembersResponse>> =>
    api.get(`/core/company-groups/${id}/members/`),

  // CR-5311: Get users across all member companies of a group
  getCompanyGroupUsers: (id: number): Promise<AxiosResponse<{ count: number; results: UserProfile[] }>> =>
    api.get(`/core/company-groups/${id}/users/`),

  // Hierarchy Tree
  getHierarchyTree: (params?: { include_inactive?: boolean; company_type?: 'customer' | 'supplier' }): Promise<AxiosResponse<HierarchyTreeResponse>> =>
    api.get('/core/hierarchy/', { params }),

  // Roles
  getRoles: (params?: QueryParams): Promise<AxiosResponse<{ results: Role[]; count: number }>> =>
    api.get('/core/roles/', { params }),

  getRole: (id: number): Promise<AxiosResponse<Role>> =>
    api.get(`/core/roles/${id}/`),

  // CR-5275: partial update. Currently used by the SecurityTab MFA toggle.
  updateRole: (id: number, data: Partial<Role>): Promise<AxiosResponse<Role>> =>
    api.patch(`/core/roles/${id}/`, data),

  // User Profiles
  getUserProfiles: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<UserProfile>>> =>
    api.get('/core/user-profiles/', { params }),

  getUserProfile: (id: number): Promise<AxiosResponse<UserProfile>> =>
    api.get(`/core/user-profiles/${id}/`),

  getMyProfile: (): Promise<AxiosResponse<UserProfile>> =>
    api.get('/core/user-profiles/me/'),

  approveUser: (id: number): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/user-profiles/${id}/approve/`),

  rejectUser: (id: number): Promise<AxiosResponse<ActionResponse>> =>
    api.post(`/core/user-profiles/${id}/reject/`),

  // Utility endpoints
  getPendingApprovals: (): Promise<AxiosResponse<{ count: number; users: UserProfile[] }>> =>
    api.get('/core/pending-approvals/'),

  getRecentRegistrations: (days?: number): Promise<AxiosResponse<{ count: number; companies: Company[] }>> =>
    api.get('/core/recent-registrations/', { params: { days } }),

  bulkApproveUsers: (userIds: number[]): Promise<AxiosResponse<ActionResponse>> =>
    api.post('/core/bulk-approve-users/', { user_ids: userIds }),

  // User Management Actions (for admins with can_manage_users permission)
  toggleUserActive: (id: number): Promise<AxiosResponse<{ status: string; is_active: boolean; message: string }>> =>
    api.post(`/core/user-profiles/${id}/toggle_active/`),

  // CR-5312: lifecycle delete. Backend hard-deletes when there's no
  // login / activity / management history; otherwise it deactivates the
  // user so the audit trail is preserved. Response carries `deleted` and
  // `deactivated` so the caller can render the right toast.
  safeDeleteUser: (id: number): Promise<AxiosResponse<{
    deleted: boolean;
    deactivated: boolean;
    message: string;
    is_active?: boolean;
    user?: UserProfile;
  }>> =>
    api.post(`/core/user-profiles/${id}/safe_delete/`),

  changeUserRole: (id: number, roleId: number): Promise<AxiosResponse<{ message: string; user: UserProfile }>> =>
    api.post(`/core/user-profiles/${id}/change_role/`, { role_id: roleId }),

  /** CR-7255: set (or clear with null) a user's role on one portal side. The
   *  customer side also moves the legacy `role`; the supplier side exists only
   *  at a dual company. */
  setUserSideRole: (
    id: number, side: 'customer' | 'supplier', roleId: number | null,
  ): Promise<AxiosResponse<{ message: string; user: UserProfile }>> =>
    api.post(`/core/user-profiles/${id}/side-role/`, { side, role_id: roleId }),

  /** Platform staff repair tool: move a user to a different company,
   *  resetting site access and clearing a role of the wrong type. */
  relinkUserCompany: (id: number, companyId: number): Promise<AxiosResponse<{ message: string; role_cleared: boolean; user: UserProfile }>> =>
    api.post(`/core/user-profiles/${id}/relink-company/`, { company_id: companyId }),

  toggleUserAdmin: (id: number, isAdmin: boolean): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/core/user-profiles/${id}/toggle_admin/`, { is_admin: isAdmin }),

  sendInvitationEmail: (
    profileId: number
  ): Promise<AxiosResponse<{ message: string; user?: UserProfile }>> =>
    api.post(`/core/user-profiles/${profileId}/send_invitation/`),

  // CR-5857: Admin-driven profile edit for managed users (Suppliers → Users).
  // Helix admins + customer admins (against suppliers they trade with) only;
  // backend enforces the same scope via CanManageUsers.has_object_permission.
  updateManagedUserProfile: (
    profileId: number,
    data: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      job_title?: string;
      department?: string;
      role?: number;  // CR-7076: clearing to null is rejected server-side
      /** ERP buyer codes for material-controller supplier scoping. */
      buyer_codes?: string[];
    }
  ): Promise<AxiosResponse<{ message: string; user: UserProfile }>> =>
    api.patch(`/core/user-profiles/${profileId}/update_profile/`, data),

  // CR-5857: Admin-triggered password reset — sends the user the same branded
  // reset email as the public Forgot Password flow and audits the action.
  adminResetUserPassword: (
    profileId: number
  ): Promise<AxiosResponse<{ message: string; email: string }>> =>
    api.post(`/core/user-profiles/${profileId}/admin_reset_password/`),

  // Set a temporary password directly on a managed customer/supplier user (no
  // email round-trip) — for when an invitation never reaches the inbox, so the
  // reset email cannot land either. Open to platform staff and customer admins;
  // the backend scopes the target to the caller's own company or a supplier
  // they trade with, and refuses staff accounts. Audited server-side.
  setTempUserPassword: (
    profileId: number,
    password: string
  ): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/core/user-profiles/${profileId}/set_temp_password/`, { password }),

  /**
   * Superuser-only: grant or revoke the platform-staff flag, so it can be
   * applied from the user card instead of Django admin. The backend refuses
   * non-superusers, self-targeting, and superuser targets.
   */
  setPlatformStaff: (
    profileId: number,
    isStaff: boolean
  ): Promise<AxiosResponse<{ message: string; is_staff: boolean }>> =>
    api.post(`/core/user-profiles/${profileId}/set_platform_staff/`, { is_staff: isStaff }),

  getUserManagementLogs: (id: number): Promise<AxiosResponse<UserManagementLog[]>> =>
    api.get(`/core/user-profiles/${id}/management_logs/`),

  getAvailableRoles: (): Promise<AxiosResponse<{ roles: Role[] }>> =>
    api.get('/core/user-profiles/available_roles/'),

  // Site Permissions
  // CR-5344: Optional companyId scopes the returned site list to that
  // company's group instead of the user's home company group. Use this
  // when opening the popup from a specific supplier's context to avoid
  // leaking the user's other-tenant site memberships.
  getUserSitePermissions: (
    userId: number,
    companyId?: number
  ): Promise<AxiosResponse<UserSitePermission[]>> =>
    api.get(`/core/user-profiles/${userId}/site_permissions/`, {
      params: companyId ? { scope_company: companyId } : undefined,
    }),

  updateUserSitePermission: (
    userId: number,
    siteId: number,
    data: Partial<UserSitePermission>
  ): Promise<AxiosResponse<UserSitePermission>> =>
    api.post(`/core/user-profiles/${userId}/site_permissions/`, { site_id: siteId, ...data }),

  // CR-6076 AC3: bulk link/unlink every site in a division/group for a user.
  // Module flags cascade as a no-clobber default; pass apply_modules_to_all=true
  // to also push them onto already-linked sites.
  assignUserGroupAccess: (
    userId: number,
    data: { group_id: number; is_linked: boolean; apply_modules_to_all?: boolean } & Partial<UserSitePermission>
  ): Promise<AxiosResponse<{ group_id: number; is_linked: boolean; applied: number[]; skipped: number[] }>> =>
    api.post(`/core/user-profiles/${userId}/assign-group/`, data),

  deleteUserSitePermission: (userId: number, siteId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/core/user-profiles/${userId}/site_permissions/${siteId}/`),

  // Primary Contacts — a user designated as THE named contact for a supplier
  // or customer company. One holder per company; a user may hold many.
  getPrimaryContacts: (params?: {
    contact_type?: 'supplier' | 'customer';
    company?: number;
    user_profile?: number;
  }): Promise<AxiosResponse<{ results: PrimaryContactAssignment[] }>> =>
    api.get('/core/primary-contacts/', { params }),

  /** Type-ahead source for the picker. Pass `user_profile` and the server
   *  restricts the list to that user's own side of the platform (a supplier
   *  user only fronts suppliers). Results are capped server-side; check
   *  `truncated` to prompt the admin to narrow their search. */
  getPrimaryContactCompanies: (params?: {
    user_profile?: number;
    contact_type?: 'supplier' | 'customer';
    search?: string;
  }): Promise<AxiosResponse<{
    count: number;
    truncated: boolean;
    results: PrimaryContactCompanyOption[];
  }>> =>
    api.get('/core/primary-contacts/available-companies/', { params }),

  /** Replace the full set of companies this user is primary contact for.
   *  Companies omitted from `companyIds` are released; companies held by
   *  someone else are taken over and reported back in `replaced`. */
  setPrimaryContacts: (
    profileId: number,
    companyIds: number[]
  ): Promise<AxiosResponse<PrimaryContactSetResponse>> =>
    api.post(`/core/primary-contacts/user/${profileId}/`, { company_ids: companyIds }),

  /** Set (or clear, with null) the primary contact for ONE company. Used by the
   *  supplier-scoped toggle on Supplier Detail → Users → Edit User Profile, so
   *  it can't disturb that user's designations for other companies. */
  setCompanyPrimaryContact: (
    companyId: number,
    profileId: number | null
  ): Promise<AxiosResponse<{
    assignment: PrimaryContactAssignment | null;
    replaced: { user_profile: number; previous_contact: string } | null;
  }>> =>
    api.post(`/core/primary-contacts/company/${companyId}/`, { user_profile: profileId }),

  // Registration
  registerCompany: (data: CompanyRegistrationData): Promise<AxiosResponse<{ message: string; company: Company }>> =>
    api.post('/core/register/company/', data),

  getRegistrationInfo: (): Promise<AxiosResponse<{
    company_types: Array<{ value: string; label: string }>;
    site_types: Array<{ value: string; label: string }>;
    warehouse_types: Array<{ value: string; label: string }>;
  }>> =>
    api.get('/core/registration-info/'),

  getCompanyTypes: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/core/company-types/'),

  // View Context - Database-backed view switching (replaces localStorage)
  getViewContext: (): Promise<AxiosResponse<ViewContext>> =>
    api.get('/core/view-context/'),

  updateViewContext: (data: Partial<ViewContextUpdate>): Promise<AxiosResponse<ViewContext>> =>
    api.patch('/core/view-context/', data),

  // CR-4802: which filter controls the viewer's tenant shows. Read by every
  // filter panel through FilterVisibilityContext; written by the Platform
  // tab's Filter Visibility card (tenant admins, platform admins in context).
  getFilterVisibility: (): Promise<AxiosResponse<FilterVisibilityPayload>> =>
    api.get('/core/settings/filter-visibility/'),

  updateFilterVisibility: (overrides: FilterVisibilityOverrides): Promise<AxiosResponse<FilterVisibilityPayload>> =>
    api.put('/core/settings/filter-visibility/', overrides),

  // Exchange Rates - Currency conversion
  /**
   * Exchange-rate history (staff/superuser only — the backend returns 403 for
   * anyone else). Paginated; rates are stored relative to GBP: 1 GBP = `rate` units.
   */
  listExchangeRates: (params: {
    currency?: string;
    date_from?: string;
    date_to?: string;
    base?: string;
    company?: number | 'platform';
    page?: number;
    page_size?: number;
  } = {}): Promise<AxiosResponse<PaginatedResponse<ExchangeRateRow>>> =>
    api.get('/core/exchange-rates/', { params }),

  /** Monthly view of the history (staff/superuser only): last rate of each month per currency. */
  getMonthlyExchangeRates: (params: {
    currency?: string;
    date_from?: string;
    date_to?: string;
    months?: number;
    base?: string;
    company?: number | 'platform';
  } = {}): Promise<AxiosResponse<MonthlyExchangeRates>> =>
    api.get('/core/exchange-rates/monthly/', { params }),

  getLatestExchangeRates: (params: { company?: number | 'platform' } = {}): Promise<AxiosResponse<{
    base_currency: string;
    rates: Record<string, string>;
    as_of: string;
  }>> =>
    api.get('/core/exchange-rates/latest/', { params }),

  getExchangeRatesForDate: (date: string): Promise<AxiosResponse<{
    base_currency: string;
    requested_date: string;
    rates: Record<string, string>;
  }>> =>
    api.get('/core/exchange-rates/for_date/', { params: { date } }),

  convertCurrency: (data: {
    amount: number;
    from_currency: string;
    to_currency: string;
    date?: string;
  }): Promise<AxiosResponse<{
    amount: string;
    from_currency: string;
    to_currency: string;
    converted_amount: string;
    exchange_rate: string;
    date: string;
  }>> =>
    api.post('/core/exchange-rates/convert/', data),

  // Version & Changelog
  getVersion: (): Promise<AxiosResponse<{
    version: string;
    build_date: string;
    build_number: number;
    git_commit: string;
    release_notes: string;
    server_time: string;
    /** CR-8222: when the Heroku build of the running code ran; null when there is no build record (local development). */
    built_at: string | null;
  }>> =>
    api.get('/core/version/'),

  getChangelog: (): Promise<AxiosResponse<{ changelog: string }>> =>
    api.get('/core/changelog/'),

  getAppInfo: (): Promise<AxiosResponse<{
    version: string;
    build_date: string;
    build_number: number;
    git_commit: string;
    release_notes: string;
    server_time: string;
    /** CR-8222: when the Heroku build of the running code ran; null when there is no build record. */
    built_at: string | null;
    changelog: string;
  }>> =>
    api.get('/core/app-info/'),

  // Legal Content
  getLegalContent: (): Promise<AxiosResponse<LegalContent>> =>
    api.get('/core/legal/'),

  acceptLegal: (data: LegalAcceptRequest): Promise<AxiosResponse<LegalAcceptResponse>> =>
    api.post('/core/legal/accept/', data),

  // Supplier-Customer Relationships
  getSupplierRelationships: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierCustomerRelationship[] }>> =>
    api.get('/core/supplier-relationships/', { params }),

  updateSupplierRelationship: (id: number, data: Partial<SupplierCustomerRelationship>): Promise<AxiosResponse<SupplierCustomerRelationship>> =>
    api.patch(`/core/supplier-relationships/${id}/`, data),

  // Customer review 11 Sep (item 33): Strategic / Call-Off are decided once
  // per supplier — write the flag to every listed site in one transaction.
  setSupplierRelationshipFlag: (data: {
    supplier: number;
    field: 'strategic' | 'call_off_enabled';
    value: boolean;
    customer_ids: number[];
  }): Promise<AxiosResponse<SupplierCustomerRelationship[]>> =>
    api.post('/core/supplier-relationships/set-supplier-flag/', data),

  /**
   * Make a supplier the Logistics Provider (carrier) of the listed customer
   * sites, or stop. On needs an active relationship on each site.
   * @param data - supplier id, on/off, and the organisation's site ids
   * @returns the supplier's relationship rows for those sites
   */
  setSupplierLogisticsProvider: (data: {
    supplier: number;
    value: boolean;
    customer_ids: number[];
  }): Promise<AxiosResponse<SupplierCustomerRelationship[]>> =>
    api.post('/core/supplier-relationships/set-logistics-provider/', data),

  // supplier_code/currency seed the relationship's primary code on commit.
  createSupplierRelationship: (data: { supplier: number; customer: number; supplier_code?: string; currency?: string }): Promise<AxiosResponse<SupplierCustomerRelationship>> =>
    api.post('/core/supplier-relationships/', data),

  deleteSupplierRelationship: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/supplier-relationships/${id}/`),

  // CR-6794: Customer Supplier Codes — the codes issued under one relationship.
  // The relationship serializer already nests these, so the list call is only
  // needed when refreshing codes without refetching the whole relationship.
  getSupplierCodes: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CustomerSupplierCode[] }>> =>
    api.get('/core/supplier-codes/', { params }),

  createSupplierCode: (data: CustomerSupplierCodeCreate): Promise<AxiosResponse<CustomerSupplierCode>> =>
    api.post('/core/supplier-codes/', data),

  updateSupplierCode: (id: number, data: CustomerSupplierCodeUpdate): Promise<AxiosResponse<CustomerSupplierCode>> =>
    api.patch(`/core/supplier-codes/${id}/`, data),

  // Archives rather than deletes — codes stay referenced by order history.
  // Refused with 400 on the primary code, which must be swapped away first.
  archiveSupplierCode: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/supplier-codes/${id}/`),

  // Its own endpoint because the swap has to be one transaction: PATCHing
  // is_primary onto a second row trips the one-primary-per-relationship
  // constraint before the old primary can be cleared.
  setPrimarySupplierCode: (id: number): Promise<AxiosResponse<CustomerSupplierCode>> =>
    api.post(`/core/supplier-codes/${id}/set-primary/`),

  // System Schedule (Platform Admin) — read-only roster of this instance's
  // scheduled jobs (celery beat) + forecast snapshot freshness.
  getSystemSchedule: (): Promise<AxiosResponse<SystemSchedule>> =>
    api.get('/core/admin/system-schedule/'),
};

/** One scheduled job from the instance's celery beat table. */
export interface SystemScheduledTask {
  name: string;
  task: string;
  enabled: boolean;
  schedule_type: 'crontab' | 'interval' | 'clocked' | 'solar' | 'unknown';
  /** Human-readable schedule, e.g. "cron 0 3 * * * (UTC)" or "every 30 seconds". */
  schedule: string;
  last_run_at: string | null;
  total_run_count: number;
  queue: string;
  description: string;
}

/** Forecast snapshot freshness for one customer. */
export interface SystemForecastSnapshot {
  customer: string;
  last_refreshed_at: string | null;
  last_status: 'ok' | 'running' | 'failed';
  last_error: string;
  last_row_count: number | null;
  last_duration_seconds: number | null;
}

export interface SystemSchedule {
  server_time: string;
  periodic_tasks: SystemScheduledTask[];
  forecast_snapshots: SystemForecastSnapshot[];
}
